import { SCError, UserPersonaErrorType } from "@next-platform/packages-ts-types";
import { getPersonaLimit } from "../../../utils/getPersonaLimit";
import { useApp } from "../../app";
import { useTranslation } from "../../localization/hooks";
import useUserPlanName from "../subscription/useUserPlanName";
import { UserRoles } from "../types";
import { PersonaLimit } from "../userPersona";
import useUserInfo from "../useUserInfo";
const { ERROR_CODE_PREFIX } = SCError || {};
// error messages will be updated at once after confirmation from @product
// Ticket: https://nexday-ai.atlassian.net/browse/NX-3260
const getNonContextUserPersonaErrors = (t) => ({
    [`${ERROR_CODE_PREFIX}-${UserPersonaErrorType.CANNOT_DELETE_DEFAULT}`]: t("error:persona.cannotDeleteDefault"),
    [`${ERROR_CODE_PREFIX}-${UserPersonaErrorType.CANNOT_DELETE_PROFILE_PERSONA}`]: t("error:persona.cannotDeleteProfilePersona"),
    [`${ERROR_CODE_PREFIX}-${UserPersonaErrorType.INVALID_AVATAR_REFERENCE}`]: t("error:persona.invalidAvatarReference"),
    [`${ERROR_CODE_PREFIX}-${UserPersonaErrorType.INVALID_REQUEST_SCHEMA}`]: t("error:persona.invalidRequestSchema"),
    [`${ERROR_CODE_PREFIX}-${UserPersonaErrorType.NOT_FOUND}`]: t("error:persona.notFound"),
    [`${ERROR_CODE_PREFIX}-${UserPersonaErrorType.NSFW_CONTENT_FOUND}`]: t("error:persona.nsfwContentFound"),
    [`${ERROR_CODE_PREFIX}-${UserPersonaErrorType.PERSONA_ID_REQUIRED}`]: t("error:persona.personaIdRequired"),
    [`${ERROR_CODE_PREFIX}-${UserPersonaErrorType.UNDER_AGE_CONTENT_FOUND}`]: t("error:persona.underAgeContentFound"),
});
// eslint-disable-next-line import/prefer-default-export -- named hook export matches package barrel
export function usePersonaError() {
    const { isPartner } = useApp();
    const { role } = useUserInfo();
    const { t } = useTranslation();
    const planName = useUserPlanName(role);
    const nonContextUserPersonaErrors = getNonContextUserPersonaErrors(t);
    const getMessage = ({ errorCode, fallbackMessage, skipDefaultMessage }) => {
        const nonContextError = nonContextUserPersonaErrors[errorCode];
        if (nonContextError) {
            return nonContextError;
        }
        else {
            // global context based errors
            switch (errorCode) {
                case `${ERROR_CODE_PREFIX}-${UserPersonaErrorType.PERSONA_LIMIT_REACHED}`:
                    const limit = isPartner ? PersonaLimit.PAID_PREMIUM : getPersonaLimit(role || UserRoles.FREE);
                    const isHighestTier = role == UserRoles.PREMIUM || isPartner;
                    if (isPartner) {
                        return t("error:persona.personaLimitReached.partner", { limit });
                    }
                    else if (isHighestTier) {
                        return t("error:persona.personaLimitReached.highestTier", {
                            limit,
                            tier: planName,
                        });
                    }
                    else {
                        return t("error:persona.personaLimitReached.upgradeable", {
                            limit,
                            tier: planName,
                        });
                    }
                default:
                    if (skipDefaultMessage)
                        return "";
                    return fallbackMessage || t("error:fallbackError");
            }
        }
    };
    return {
        getMessage,
    };
}
